import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FiCreditCard, FiDollarSign } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';

const ContractTabs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeContractType, setActiveContractType] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActiveContract = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Vérifier si l'utilisateur a un contrat actif en utilisant les mêmes méthodes que BlockingOverlay
        const [subscriptionRes, commissionRes] = await Promise.all([
          api.getActiveSubscription(user.id).catch(e => {
            console.error('Erreur lors de la vérification de l\'abonnement:', e);
            return { data: null };
          }),
          api.getActiveCommission(user.id).catch(e => {
            console.error('Erreur lors de la vérification de la commission:', e);
            return { data: null };
          })
        ]);

        const subscription = subscriptionRes?.data?.data || subscriptionRes?.data || null;
        const commission = commissionRes?.data?.data || commissionRes?.data || null;

        console.log('[ContractTabs] Vérification des contrats:', {
          userId: user.id,
          hasSubscription: !!subscription,
          hasCommission: !!commission
        });

        if (subscription) {
          setActiveContractType('subscription');
          console.log('[ContractTabs] Abonnement actif détecté - Forcer navigation vers abonnement');
          // Forcer la redirection vers l'onglet abonnement si on est sur commission
          if (location.pathname.endsWith('commission')) {
            console.log('[ContractTabs] Redirection forcée de commission vers abonnement');
            navigate('/demenageur/contrat/abonnement', { replace: true });
          } else if (!location.pathname.endsWith('abonnement')) {
            navigate('abonnement', { replace: true });
          }
        } else if (commission) {
          setActiveContractType('commission');
          console.log('[ContractTabs] Commission active détectée - Forcer navigation vers commission');
          // Forcer la redirection vers l'onglet commission si on est sur abonnement
          if (location.pathname.endsWith('abonnement')) {
            console.log('[ContractTabs] Redirection forcée d\'abonnement vers commission');
            navigate('/demenageur/contrat/commission', { replace: true });
          } else if (!location.pathname.endsWith('commission')) {
            navigate('commission', { replace: true });
          }
        } else {
          setActiveContractType(null);
          // Rediriger vers l'onglet abonnement par défaut si aucun contrat
          if (!location.pathname.endsWith('abonnement') && !location.pathname.endsWith('commission')) {
            navigate('abonnement', { replace: true });
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des contrats:', error);
        setActiveContractType(null);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchActiveContract();
    }
  }, [user, user?.id, location.pathname, navigate]);

  const isActive = (path) => location.pathname.endsWith(path);

  // Définir les onglets disponibles en fonction du type de contrat actif
  const getAvailableTabs = () => {
    if (loading) return [];
    
    // Si l'utilisateur n'a pas de contrat, afficher les deux onglets
    if (!activeContractType) {
      return [
        { id: 'abonnement', label: 'Abonnement', icon: <FiCreditCard className="mr-2" />, path: 'abonnement' },
        { id: 'commission', label: 'Commission', icon: <FiDollarSign className="mr-2" />, path: 'commission' },
      ];
    }
    
    // Si l'utilisateur a un contrat d'abonnement, n'afficher que l'onglet abonnement
    if (activeContractType === 'subscription') {
      return [
        { id: 'abonnement', label: 'Abonnement', icon: <FiCreditCard className="mr-2" />, path: 'abonnement' },
      ];
    }
    
    // Si l'utilisateur a un contrat de commission, n'afficher que l'onglet commission
    return [
      { id: 'commission', label: 'Commission', icon: <FiDollarSign className="mr-2" />, path: 'commission' },
    ];
  };

  const tabs = getAvailableTabs();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  // Fonction pour vérifier si un onglet peut être cliqué
  const canAccessTab = (tabId) => {
    if (loading) return false;
    
    // Si on a un abonnement actif, on ne peut accéder qu'à l'onglet abonnement
    if (activeContractType === 'subscription' && tabId === 'commission') {
      return false;
    }
    
    // Si on a une commission active, on ne peut accéder qu'à l'onglet commission
    if (activeContractType === 'commission' && tabId === 'abonnement') {
      return false;
    }
    
    return true;
  };

  const handleTabClick = (e, tab) => {
    // Empêcher la navigation si l'onglet n'est pas accessible
    if (!canAccessTab(tab.id)) {
      e.preventDefault();
      console.log(`[ContractTabs] Accès bloqué à l'onglet ${tab.id} - Contrat actif: ${activeContractType}`);
      
      // Rediriger vers l'onglet autorisé
      if (activeContractType === 'subscription') {
        navigate('/demenageur/contrat/abonnement', { replace: true });
      } else if (activeContractType === 'commission') {
        navigate('/demenageur/contrat/commission', { replace: true });
      }
      return;
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {tabs.length > 0 && (
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-4" aria-label="Contract tabs">
            {tabs.map((tab) => {
              const isAccessible = canAccessTab(tab.id);
              return (
                <NavLink
                  key={tab.id}
                  to={tab.path}
                  onClick={(e) => handleTabClick(e, tab)}
                  className={`
                    flex items-center px-4 py-2 text-sm font-medium rounded-t-lg
                    ${isActive(tab.path) 
                      ? 'bg-white text-blue-600 border-b-2 border-blue-500' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
                    ${!isAccessible ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}
                  `}
                >
                  {tab.icon}
                  {tab.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      )}
      <Outlet />
    </div>
  );
};

export default ContractTabs;
